import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Command, Clock, Star, ArrowRight } from "lucide-react";

interface SearchItem {
  label: string;
  path: string;
  section: string;
  keywords: string[];
}

const SEARCH_INDEX: SearchItem[] = [
  // Mission Control
  { label: "Mission Control", path: "/mission-control", section: "Mission Control", keywords: ["home", "dashboard", "executive"] },
  { label: "Action Center", path: "/mission-control/actions", section: "Mission Control", keywords: ["tasks", "approvals", "pending"] },
  // Commercial
  { label: "Opportunities", path: "/app/commercial/opportunities", section: "Commercial", keywords: ["sales", "pipeline", "deals"] },
  { label: "Proposals", path: "/app/commercial/proposals", section: "Commercial", keywords: ["quotations", "offers", "bids"] },
  { label: "Clients", path: "/app/commercial/clients", section: "Commercial", keywords: ["customers", "accounts"] },
  { label: "Client POs", path: "/app/commercial/client-pos", section: "Commercial", keywords: ["purchase orders", "orders"] },
  { label: "Pipeline Forecast", path: "/app/commercial/pipeline-forecast", section: "Commercial", keywords: ["forecast", "revenue"] },
  { label: "Sales Performance", path: "/app/commercial/sales-performance", section: "Commercial", keywords: ["kpi", "metrics"] },
  // Projects & Operations
  { label: "Projects", path: "/app/projects/list", section: "Projects & Operations", keywords: ["operations", "project list"] },
  { label: "Timesheets", path: "/app/projects/timesheets", section: "Projects & Operations", keywords: ["time", "hours"] },
  { label: "Project P&L", path: "/app/projects/pnl", section: "Projects & Operations", keywords: ["profit", "loss"] },
  { label: "Milestones", path: "/app/projects/milestones", section: "Projects & Operations", keywords: ["certificates", "progress"] },
  { label: "Delivery Notes", path: "/app/projects/delivery-notes", section: "Projects & Operations", keywords: ["delivery", "goods", "shipment"] },
  // Procurement
  { label: "Purchase Orders", path: "/app/procurement/purchase-orders", section: "Procurement", keywords: ["po", "buying"] },
  { label: "RFQs", path: "/app/procurement/rfqs", section: "Procurement", keywords: ["request for quotation"] },
  { label: "Vendors", path: "/app/procurement/vendors", section: "Procurement", keywords: ["suppliers"] },
  { label: "GRN", path: "/app/procurement/grn", section: "Procurement", keywords: ["goods receipt", "delivery"] },
  // Inventory
  { label: "Inventory", path: "/app/inventory", section: "Inventory", keywords: ["stock", "warehouse", "items"] },
  // Finance
  { label: "Finance Dashboard", path: "/app/finance", section: "Finance", keywords: ["financial", "overview"] },
  { label: "Invoices", path: "/app/finance/invoices", section: "Finance", keywords: ["billing", "ar"] },
  { label: "P&L Statement", path: "/app/finance/pl-statement", section: "Finance", keywords: ["profit", "loss", "income"] },
  { label: "Cash Flow", path: "/app/finance/cash-flow", section: "Finance", keywords: ["liquidity"] },
  { label: "Balance Sheet", path: "/app/finance/balance-sheet", section: "Finance", keywords: ["assets", "liabilities"] },
  { label: "Trial Balance", path: "/app/finance/trial-balance", section: "Finance", keywords: ["gl", "ledger"] },
  { label: "ZATCA Onboarding", path: "/app/finance/zatca-onboarding", section: "Finance", keywords: ["fatoora", "e-invoicing", "csr", "otp", "egs", "zatca"] },
  // HR
  { label: "HR Dashboard", path: "/app/hr", section: "People & HR", keywords: ["human resources", "employees"] },
  { label: "Employees", path: "/app/hr/employees", section: "People & HR", keywords: ["staff", "team"] },
  { label: "Training", path: "/app/hr/training", section: "People & HR", keywords: ["learning", "development"] },
  // QHSE
  { label: "QHSE Documents", path: "/app/qhse/documents", section: "QHSE", keywords: ["quality", "safety", "ims"] },
  { label: "Audits", path: "/app/qhse/audits", section: "QHSE", keywords: ["audit", "inspection"] },
  { label: "CAPA", path: "/app/qhse/capa", section: "QHSE", keywords: ["corrective", "preventive", "action"] },
  // Platform
  { label: "Configuration Center", path: "/platform/configuration", section: "Platform", keywords: ["config", "settings"] },
  { label: "Security Center", path: "/platform/security", section: "Platform", keywords: ["rbac", "permissions", "roles"] },
  { label: "Audit Center", path: "/platform/audit", section: "Platform", keywords: ["audit log", "trail"] },
  { label: "Release Center", path: "/platform/releases", section: "Platform", keywords: ["version", "deploy"] },
];

const PINNED_KEY = "energipro_pinned_pages";
const RECENT_KEY = "energipro_recent_pages";

function getPinned(): string[] {
  try {
    return JSON.parse(localStorage.getItem(PINNED_KEY) || "[]");
  } catch { return []; }
}

function getRecent(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
  } catch { return []; }
}

function addRecent(path: string) {
  const recent = getRecent().filter(r => r !== path);
  recent.unshift(path);
  localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, 10)));
}

function togglePin(path: string) {
  const pinned = getPinned();
  const idx = pinned.indexOf(path);
  if (idx >= 0) pinned.splice(idx, 1);
  else pinned.push(path);
  localStorage.setItem(PINNED_KEY, JSON.stringify(pinned));
}

export function EnterpriseSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIdx, setSelectedIdx] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  // ⌘K shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen(prev => !prev);
      }
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQuery("");
      setSelectedIdx(0);
    }
  }, [open]);

  const results = query.trim()
    ? SEARCH_INDEX.filter(item => {
        const q = query.toLowerCase();
        return (
          item.label.toLowerCase().includes(q) ||
          item.section.toLowerCase().includes(q) ||
          item.keywords.some(k => k.includes(q))
        );
      })
    : [];

  const pinned = getPinned();
  const recent = getRecent();
  const pinnedItems = SEARCH_INDEX.filter(i => pinned.includes(i.path));
  const recentItems = SEARCH_INDEX.filter(i => recent.includes(i.path)).slice(0, 5);

  const displayItems = query.trim() ? results : [...pinnedItems, ...recentItems];

  const handleSelect = useCallback((path: string) => {
    addRecent(path);
    navigate(path);
    setOpen(false);
  }, [navigate]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIdx(i => Math.min(i + 1, displayItems.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIdx(i => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && displayItems[selectedIdx]) {
      handleSelect(displayItems[selectedIdx].path);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 text-sm transition-colors border border-gray-200"
      >
        <Search className="h-4 w-4" />
        <span className="hidden md:inline">Search...</span>
        <kbd className="hidden md:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-mono bg-gray-200 rounded">
          <Command className="h-2.5 w-2.5" />K
        </kbd>
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <div className="relative w-full max-w-lg bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
        {/* Search Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
          <Search className="h-5 w-5 text-gray-400 flex-shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => { setQuery(e.target.value); setSelectedIdx(0); }}
            onKeyDown={handleKeyDown}
            placeholder="Search pages, commands, and more..."
            className="flex-1 text-sm bg-transparent outline-none text-gray-900 placeholder:text-gray-400"
          />
          <kbd className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded font-mono">ESC</kbd>
        </div>

        {/* Results */}
        <div className="max-h-80 overflow-y-auto py-2">
          {!query.trim() && pinnedItems.length > 0 && (
            <div className="px-3 py-1">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-1 mb-1">
                <Star className="h-3 w-3" /> Pinned
              </p>
            </div>
          )}
          {!query.trim() && recentItems.length > 0 && pinnedItems.length === 0 && (
            <div className="px-3 py-1">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-1 mb-1">
                <Clock className="h-3 w-3" /> Recent
              </p>
            </div>
          )}
          {displayItems.length === 0 && query.trim() && (
            <div className="px-4 py-8 text-center text-sm text-gray-400">
              No results for &ldquo;{query}&rdquo;
            </div>
          )}
          {displayItems.length === 0 && !query.trim() && (
            <div className="px-4 py-8 text-center text-sm text-gray-400">
              Type to search across the platform
            </div>
          )}
          {displayItems.map((item, idx) => (
            <button
              key={item.path}
              onClick={() => handleSelect(item.path)}
              onMouseEnter={() => setSelectedIdx(idx)}
              className={`w-full flex items-center gap-3 px-4 py-2 text-left text-sm transition-colors ${
                idx === selectedIdx ? "bg-blue-50 text-blue-700" : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 opacity-50" />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{item.label}</p>
                <p className="text-[10px] text-gray-400">{item.section}</p>
              </div>
              <button
                onClick={e => { e.stopPropagation(); togglePin(item.path); }}
                className="p-1 rounded hover:bg-gray-200"
                title={pinned.includes(item.path) ? "Unpin" : "Pin"}
              >
                <Star className={`h-3 w-3 ${pinned.includes(item.path) ? "fill-yellow-400 text-yellow-500" : "text-gray-300"}`} />
              </button>
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 px-4 py-2 flex items-center gap-4 text-[10px] text-gray-400">
          <span>↑↓ Navigate</span>
          <span>↵ Open</span>
          <span>⌘K Toggle</span>
        </div>
      </div>
    </div>
  );
}
